/**
 * normalizeSite.js
 *
 * Pure helpers that turn whatever shape the public `/public/site` endpoint
 * returns into the flat site records the search field renders. Kept UI- and
 * data-layer-free so they can be unit tested directly.
 */

/**
 * @typedef {Object} PublicSite
 * @property {number|string} id
 * @property {string} name
 * @property {string} [addressLine1]
 * @property {string} [addressLine2]
 * @property {string} [city]
 * @property {string} [state]
 * @property {string} [zipCode]
 * @property {boolean} [isActive]
 */

/**
 * Pulls the list of site records out of whatever envelope the endpoint uses:
 * a bare array, or an object keyed by `data` / `sites` / `results`.
 *
 * @param {*} payload - The parsed JSON response.
 * @returns {Array} The array of raw site records (empty if none found).
 */
export function extractList(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === 'object') {
    if (Array.isArray(payload.data)) return payload.data;
    if (Array.isArray(payload.sites)) return payload.sites;
    if (Array.isArray(payload.results)) return payload.results;
  }
  return [];
}

/**
 * Normalizes a raw site record to the flat shape the search field renders.
 * Tolerates a Lore-style `{ id, data: { ... } }` envelope as well as a
 * flat record.
 *
 * @param {Object} record - A raw site record from the endpoint.
 * @returns {PublicSite} The normalized site.
 */
export function normalizeSite(record) {
  const data = record?.data && typeof record.data === 'object' ? record.data : record;
  return {
    id: record?.id ?? data?.id,
    name: data?.name ?? '',
    addressLine1: data?.addressLine1 ?? '',
    addressLine2: data?.addressLine2 ?? '',
    city: data?.city ?? '',
    state: data?.state ?? '',
    zipCode: data?.zipCode ?? '',
    isActive: data?.isActive ?? false,
  };
}
